import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import TenantList from './superadmin/TenantList';
import TenantForm from './superadmin/TenantForm';
import { runMigration } from './firebase/migration';
import './App.css';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, loading, isSuperAdmin, logOut } = useAuth();
  const [view, setView] = useState('list');
  const [editingTenant, setEditingTenant] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);

  if (loading) {
    return <div className="dashboard-container"><div className="super-admin-loading">Loading...</div></div>;
  }
  if (!user) {
    navigate('/adminlogin');
    return null;
  }
  if (!isSuperAdmin()) {
    navigate('/dashboard');
    return null;
  }

  const handleLogout = async () => {
    await logOut();
    navigate('/adminlogin');
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setView('form');
  };

  const handleCreateNew = () => {
    setEditingTenant(null);
    setView('form');
  };

  const handleFormSaved = () => {
    setEditingTenant(null);
    setView('list');
  };

  const handleFormCancel = () => {
    setEditingTenant(null);
    setView('list');
  };

  const handleRunMigration = async () => {
    if (!window.confirm('Copy existing root data (members, payments, contacts, reminders) into the default tenant "De Muscle Factory"? This is for one-time migration to multi-tenant.')) return;
    setMigrating(true);
    setMigrationStatus(null);
    const result = await runMigration();
    setMigrationStatus(result);
    setMigrating(false);
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-logo">
            <div className="logo-icon">💪</div>
            <span className="logo-text">Platform</span>
            <span className="dashboard-subtitle">Super Admin</span>
          </div>
          <div className="dashboard-user-info">
            <span className="user-email">{user.email}</span>
            <button type="button" onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>
      <nav className="dashboard-nav">
        <button
          type="button"
          className={`nav-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Tenants
        </button>
        <button
          type="button"
          className="nav-tab"
          onClick={() => setView('migration')}
        >
          Migration
        </button>
      </nav>
      <main className="dashboard-main">
        {view === 'list' && (
          <TenantList onEdit={handleEdit} onCreateNew={handleCreateNew} />
        )}
        {view === 'migration' && (
          <div className="tenant-form-container" style={{ padding: '1.5rem' }}>
            <h2>Data Migration</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1rem' }}>
              Copy existing data from root (members, payments, contacts, reminders) into the default tenant &quot;De Muscle Factory&quot; (slug: demusclefactory). Run once when moving to multi-tenant.
            </p>
            <button type="button" className="super-admin-btn primary" onClick={handleRunMigration} disabled={migrating}>
              {migrating ? 'Running...' : 'Run migration'}
            </button>
            {migrationStatus && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: migrationStatus.success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)', borderRadius: 8 }}>
                {migrationStatus.success ? (
                  <p>Done. Tenant ID: {migrationStatus.tenantId}. Copied: members {migrationStatus.copied?.members ?? 0}, payments {migrationStatus.copied?.payments ?? 0}, contacts {migrationStatus.copied?.contacts ?? 0}, reminders {migrationStatus.copied?.reminders ?? 0}.</p>
                ) : (
                  <p>Error: {migrationStatus.message}</p>
                )}
              </div>
            )}
            <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>
              After migration: set your existing admin in Firebase Realtime Database → users/YOUR_UID to &quot;role&quot;: &quot;tenant_admin&quot;, &quot;tenantId&quot;: &quot;&lt;tenantId&gt;&quot;, &quot;email&quot;: &quot;...&quot;. Create a separate Auth user for super admin and set users/NEW_UID to &quot;role&quot;: &quot;super_admin&quot;.
            </p>
          </div>
        )}
        {view === 'form' && (
          <TenantForm
            tenant={editingTenant}
            onSaved={handleFormSaved}
            onCancel={handleFormCancel}
          />
        )}
      </main>
    </div>
  );
}

export default SuperAdminDashboard;
